import { Box, ButtonProps, useMediaQuery, useTheme } from "@mui/material";
import { GridAddIcon } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ReactComponent as Export } from "../../assets/icons/export.svg";
import { COLORS } from "../../assets/theme";
import {
  getStepsData,
  getStepLabels,
  getHasHttp,
  IStepDialogData,
} from "../../components/AccountFields";
import Actions from "../../components/common/Actions";
import { ICustomButtonProps } from "../../components/common/CustomButton";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import ToastNotification from "../../components/ToastNotification";
import {
  addAccountApi,
  deleteAccountApi,
  editAccountApi,
  exportAccount,
  exportSessionLogs,
  fetchAccountHistory,
  getAccountById,
  IAccount,
  useAccountsQuery,
} from "../../services/AccountService";
import { useProfilesQuery } from "../../services/ProfilesService";
import {
  addProviderApi,
  deleteProviderApi,
  editProviderApi,
  IProvider,
} from "../../services/ProviderService";
import { useRoutingPlansQuery } from "../../services/RoutingPlanService";
import { useSmppScheduleDeliveryTimeQuery } from "../../services/SmppScheduleDeliveryTimeService";
import { useVpnTunnelsAllQuery } from "../../services/VpnTunnelService";
import { IField, Option, Params } from "../../types/table/interfaces.type";
import TelnetDialogContent from "../../components/TelnetDialogContent";
import { LampCell } from "../../components/CustomLamp";

import History from "../../components/History";
import SearchForm from "../../components/common/SearchForm";
import { useUsersByEmailQuery } from "../../services/UsersService";
import {
  useLiveBind,
  useLiveBindsPolling,
} from "../../services/LiveBindsStore";
import { IAutoCompleteProps } from "../../components/common/AutoComplete";

import Dialog from "@mui/material/Dialog";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import ConditionValuesField from "../../components/ConditionValuesField";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { hasJwtPermission } from "../../services/ServicesHelpers";

/**
the table body is the only part of the tree
 * that re-renders on the 1s metrics tick. Keeping `liveBinds` out of the
 * AccountPage state prevents the SearchForm's AutoComplete inputs from being
 * re-rendered every second
 */
const BindLampCell: React.FC<{ row: IAccount }> = React.memo(({ row }) => {
  const bindsValue = useLiveBind(row.id);
  const firstChar = String(bindsValue ?? "0/0").charAt(0);
  const isUp = Number(firstChar) > 0;
  return (
    <div style={{ marginLeft: "40px" }}>
      <LampCell isUp={isUp} error={row.lastBindError} />
    </div>
  );
});
BindLampCell.displayName = "BindLampCell";

const BindValueCell: React.FC<{
  id: string | number | undefined | null;
  fallback?: React.ReactNode;
}> = React.memo(({ id, fallback = "N/A" }) => {
  const bindsValue = useLiveBind(id);
  return <>{bindsValue ?? fallback}</>;
});
BindValueCell.displayName = "BindValueCell";

const AccountPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editAccountId = searchParams.get("edit");
  const [userOptions, setUserOptions] = useState<Option[]>([]);

  const [appliedFilters, setAppliedFilters] = useState<Params<IAccount>>({});
  const [filters, setFilters] = useState<
    Params<
      IAccount & {
        countryId?: string;
        connectionProfileId?: string;
        vendorId?: string;
        destinationOperatorId?: number;
        userId?: string;
        active?: boolean;
        currencyId?: string;
      }
    >
  >({
    countryId: undefined,
    connectionProfileId: undefined,
    vendorId: undefined,
    destinationOperatorId: undefined,
    userId: undefined,
  });
  const [conditionStatuses, setConditionStatuses] = useState<
    {
      conditionType?: string;
      status?: string;
      field?: string;
      value?: string;
    }[]
  >([{ conditionType: "Equals", status: "", field: "", value: "" }]);
  const [profileOptions, setProfileOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [profileMap, setProfileMap] = useState<
    Record<
      string,
      {
        name: string;
        accountManagerEmail: string;
        currencyId: string;
        usesVpn?: boolean;
      }
    >
  >({});
  const [routingPlanOptions, setRoutingPlanOptions] = useState<Option[]>([]);
  const [vpnTunnelOptions, setVpnTunnelOptions] = useState<Option[]>([]);
  const [smppScheduleDeliveryTimeOptions, setSmppScheduleDeliveryTimeOptions] =
    useState<Option[]>([]);

  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<IAccount | null>(null);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    rowId: string | number;
  } | null>(null);

  const dialogRef = useRef<AccountDialogRef>(null);
  const handleOpenDialog = (
    mode: "add" | "edit" | "delete" | "telnet" | "history",
    id?: string,
    rowType?: string,
    isClone = false,
  ) => {
    dialogRef.current?.openDialog(mode, id, isClone, rowType);
  };

  // Handle context menu
  const handleContextMenu = (
    event: React.MouseEvent,
    rowId: string | number,
  ) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
            rowId,
          }
        : null,
    );
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  const handleOpenInNewTab = () => {
    if (contextMenu) {
      const url = `/companies/account?edit=${contextMenu.rowId}`;
      window.open(url, "_blank");
      handleContextMenuClose();
    }
  };

  const { data } = useAccountsQuery({ size: 10000 });
  const names =
    data?.content?.map((account) => ({
      label: account.name,
      value: account.id,
    })) || [];
  const types = [
    { label: "CLIENT", value: "CLIENT" },
    { label: "VENDOR", value: "VENDOR" },
  ];
  const statusOptions = [
    { label: "Active", value: "true" },
    { label: "Inactive", value: "false" },
  ];

  const { data: profileOption } = useProfilesQuery();
  const { data: routingPlansOptions } = useRoutingPlansQuery();
  const { data: vpnTunnelsOptions } = useVpnTunnelsAllQuery();
  const { data: smppScheduleDeliveryTimeOptionsData } =
    useSmppScheduleDeliveryTimeQuery();
  const { data: fetchUsersByEmail } = useUsersByEmailQuery();

  useEffect(() => {
    if (fetchUsersByEmail) {
      setUserOptions(fetchUsersByEmail);
    }
    if (profileOption) {
      setProfileOptions(profileOption.profileOptions);
      setProfileMap(profileOption.profileMap);
    }
    if (routingPlansOptions) {
      setRoutingPlanOptions(routingPlansOptions);
    }
    if (vpnTunnelsOptions) {
      setVpnTunnelOptions(vpnTunnelsOptions);
    }
    if (smppScheduleDeliveryTimeOptionsData) {
      setSmppScheduleDeliveryTimeOptions(smppScheduleDeliveryTimeOptionsData);
    }
  }, [
    profileOption,
    routingPlansOptions,
    fetchUsersByEmail,
    vpnTunnelsOptions,
    smppScheduleDeliveryTimeOptionsData,
  ]);

  // Auto-open edit dialog when ID is in query parameters (for new tab functionality)
  useEffect(() => {
    if (editAccountId && dialogRef.current) {
      handleOpenDialog("edit", editAccountId);
    }
  }, [editAccountId]);

  const actions = Actions({
    handleOpenEditModal: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id));
      },
      onContextMenu: handleContextMenu,
      isDisabled:
        !hasJwtPermission("GET_CONNECTION_PROFILE") &&
        !hasJwtPermission("GET_CONNECTION_PROFILE"),
    },
    onDelete: {
      handler: async (id: string | number) => {
        console.log(`Delete action triggered for ID: ${id}`);
        handleOpenDialog("delete", String(id));
      },
      isDisabled: !hasJwtPermission("DELETE_CONNECTION_PROFILE"),
    },
    onCloneAction: {
      handler: async (id: string | number) => {
        console.log(`Clone action triggered for ID: ${id}`);
        handleOpenDialog("edit", String(id), undefined, true);
      },
      isDisabled: !hasJwtPermission("POST_CONNECTION_PROFILE"),
    },
    onTelnetAction: {
      handler: (row: any) => {
        console.log(`onTelnetAction action triggered for ID: ${row.id}`);
        const connectionId = String(row.id);
        const rowType = row.type;
        handleOpenDialog("telnet", connectionId, rowType);
      },
      isDisabled: !hasJwtPermission("GET_ACCOUNT_LOGS"),
    },
    onViewHistory: {
      // isDisabled: true,
      handler: async (id: number | string) => {
        handleOpenDialog("history", String(id));
      },
      isDisabled: !hasJwtPermission("GET_ALL_CONNECTION_PROFILES_HISTORY"),
    },
    onPcapTrace: {
      handler: (row: any) => {
        const accountId = String(row.id);
        const ipAddress = row.acceptedIps || row.ip || "";
        navigate(
          `/message_activity/pcap_trace?accountId=${accountId}&ip=${encodeURIComponent(ipAddress)}`,
        );
      },
      isDisabled: !hasJwtPermission("GET_ALL_PCAP_TRACES"),
    },
  });

  const transformConditions = (
    conditions: {
      field?: string;
      conditionType?: string;
      status?: string;
      value?: string;
    }[],
  ): Record<string, string[]> =>
    conditions.reduce(
      (acc, { field, conditionType, status, value }) => {
        if (field && conditionType) {
          const typeCamel = conditionType.replace(/_([a-z])/g, (_, letter) =>
            letter.toUpperCase(),
          );
          const key = `${field}${typeCamel}`;
          if (!acc[key]) {
            acc[key] = [];
          }
          if (
            acc[key].length === 0 &&
            (status === undefined || status === "")
          ) {
            acc[key].push(value ?? "");
          } else {
            acc[key].push(status ?? "");
          }
        }
        return acc;
      },
      {} as Record<string, string[]>,
    );

  const { search, ...restFilters } = appliedFilters;

  const logConditionStatuses = (
    statuses?: {
      field?: string;
      conditionType?: string;
      status?: string;
      label?: string;
      value?: string;
    }[],
  ) => {
    if (!statuses) {
      console.log("No condition statuses provided");
      return;
    }

    const result: Record<string, string[]> = {};

    statuses.forEach((condition, idx) => {
      const { field, conditionType, status, value } = condition;
      const typeCamel = conditionType
        ? conditionType.replace(/_([a-z])/g, (_, letter) => letter)
        : "";
      const combinedKey = field && typeCamel ? `${field}${typeCamel}` : "";

      if (!combinedKey) return;

      if (!result[combinedKey]) {
        result[combinedKey] = [];
      }

      if (idx === 0 && (status === undefined || status === "")) {
        result[combinedKey].push(value ?? "");
      } else {
        result[combinedKey].push(status ?? "");
      }
    });
  };

  const autoCompletes: IAutoCompleteProps[] = [
    {
      label: "Name",
      value: filters.name ?? "",
      options: names,
      onChange: (value: string | number | (string | number)[]) => {
        const selectedOperator = names.find((option) => option.value === value);

        setFilters((prev) => ({
          ...prev,
          name: selectedOperator?.value,
        }));
      },
      clearable: true,
    },
    {
      label: "Type",
      value: filters.type ?? "",
      options: types,
      onChange: (value: string | number | (string | number)[]) => {
        const selectedOperator = types.find((option) => option.value === value);

        setFilters((prev) => ({
          ...prev,
          type: selectedOperator?.value,
        }));
      },
      clearable: true,
    },
    {
      label: "Status",
      value: String(filters.active ?? ""),
      options: statusOptions,
      onChange: (value: string | number | (string | number)[]) => {
        const selectedOperator = statusOptions.find(
          (option) => option.value === value,
        );

        setFilters((prev) => {
          let boolValue: boolean | undefined = undefined;
          if (selectedOperator?.value === "true") boolValue = true;
          else if (selectedOperator?.value === "false") boolValue = false;
          return { ...prev, active: boolValue };
        });
      },
      clearable: true,
    },
  ];

  const handleSearchClick = () => {
    const conditionParams = transformConditions(conditionStatuses ?? []);

    setAppliedFilters((prev) => ({
      ...filters,
      ...conditionParams,
      id: filters.name,
      type: filters.type,
      active: filters.active,
      _forceRefresh: Date.now(),
    }));
  };
  const buttons = [
    {
      label: "Search",
      sx: { marginTop: 2 },
      variant: "contained" as ButtonProps["variant"],
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      permission: "GET_ALL_CONNECTION_PROFILES",
    },
  ];

  useLiveBindsPolling(!!data?.content);

  const fields: IField<IAccount>[] = [
    {
      key: "name",
      header: "",
      transform: (row) => <BindLampCell row={row} />,
    },

    {
      key: "name",
      header: "Name",
      transform: (row) => (
        <span
          style={{
            color: COLORS.GOLD,
            cursor: "pointer",
            textDecoration: "underline",
          }}
          onClick={() => handleNameClick(row)}
        >
          {row.name}
        </span>
      ),
    },
    {
      key: "profileId",
      header: "Account Profile",
      transform: async (t) => {
        if (!t.profileId) return "Unknown";
        const profile = profileMap[t.profileId];
        return profile?.name || "Unknown";
      },
    },
    {
      key: "profileId",
      header: "Account Manager",
      transform: async (t) => {
        if (!t.profileId) return "Unknown";
        const profile = profileMap[t.profileId];
        return profile?.accountManagerEmail;
      },
    },
    { key: "type", header: "Account Type" },
    { key: "createdBy", header: "User" },

    {
      key: "id",
      header: "Binds",
      transform: (row) => <BindValueCell id={row.id} />,
    },
  ];

  const handleNameClick = (row: IAccount) => {
    setSelectedAccount(row);
    setNameDialogOpen(true);
  };

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <Box sx={{ mb: 3 }}>
        <SearchForm autoCompletes={autoCompletes} buttons={buttons}>
          {conditionStatuses && conditionStatuses.length > 0 && (
            <Box mt={2}>
              <ConditionValuesField
                whiteBackground
                notIfCondition
                valueOptions={userOptions.map((option) => ({
                  label: option.label,
                  value: String(option.value),
                }))}
                conditionTypeOptions={[
                  { label: "Contains", value: "Contains" },
                  { label: "Equals", value: "Equals" },
                ]}
                label="Sender ID"
                type="text"
                labelOptions={[
                  { label: "Profile Name", value: "profileName" },
                  {
                    label: "Username",
                    value: "SystemId",
                  },
                  {
                    label: "Employee",
                    value: "userId",
                  },
                  {
                    label: "IP",
                    value: "Ip",
                  },
                  { label: "Port", value: "Port" },
                ]}
                onLabelChange={(index, value) => {
                  setConditionStatuses((prev) => {
                    const updatedStatuses = prev.map((c, i) =>
                      i === index
                        ? { ...c, field: value, status: "", value: "" }
                        : c,
                    );
                    logConditionStatuses(updatedStatuses);
                    return updatedStatuses;
                  });
                }}
                conditionStatuses={conditionStatuses}
                handleConditionChange={(index, field, status) => {
                  setConditionStatuses((prev) => {
                    const updatedStatuses = prev.map((c, i) =>
                      i === index ? { ...c, [field]: status } : c,
                    );
                    logConditionStatuses(updatedStatuses);
                    return updatedStatuses;
                  });
                }}
                handleAddCondition={() => {
                  setConditionStatuses((prev) => [
                    ...prev,
                    { conditionType: "", status: "" },
                  ]);
                }}
                handleRemoveCondition={(index) => {
                  setConditionStatuses((prev) =>
                    prev.filter((_, i) => i !== index),
                  );
                }}
              />
            </Box>
          )}
        </SearchForm>
      </Box>
      <Box mb={2}>
        <CustomLabel
          text="Account List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginBottom={1}
        />
        <SearchHeaderContainer
          search={appliedFilters.search ?? ""}
          setSearch={(value) =>
            setAppliedFilters((prev) => ({ ...prev, search: value }))
          }
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <GridAddIcon />,
              onClick: () => handleOpenDialog("add"),
              textColor: COLORS.iconColor,
              disabled: !hasJwtPermission("POST_CONNECTION_PROFILE"),
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <Export />,
              onClick: async () => {
                try {
                  await exportAccount(appliedFilters);
                  ToastNotification.success("Export completed successfully!");
                } catch (error: any) {
                  ToastNotification.error(
                    error.message || "Export failed. Please try again.",
                  );
                }
              },
              showExportProgress: true,
              textColor: COLORS.iconColor,
              label: "Export",
              permission: "GET_ALL_CONNECTION_PROFILES_CSV",
            },
          ]}
        />
        <CustomTable<IAccount>
          filters={appliedFilters}
          fields={fields}
          fetchFunc={useAccountsQuery}
          actions={actions}
          sortableFields={["name", "type", "createdBy", "maximumConnections"]}
        />
      </Box>

      <AccountDialog
        ref={dialogRef}
        onRefresh={() => {
          setAppliedFilters((prev) => ({ ...prev }));
          queryClient.invalidateQueries();
        }}
        profileOptions={profileOptions}
        routingPlanOptions={routingPlanOptions}
        vpnTunnelOptions={vpnTunnelOptions}
        smppScheduleDeliveryTimeOptions={smppScheduleDeliveryTimeOptions}
        appliedFilters={appliedFilters}
        profileMap={profileMap}
      />
      <Dialog
        open={nameDialogOpen}
        onClose={() => setNameDialogOpen(false)}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          "& .MuiDialog-container": {
            alignItems: "flex-start",
          },
          mt: 2,
        }}
      >
        <Box p={3} position="relative" sx={{ width: "300px", maxWidth: 500 }}>
          <IconButton
            aria-label="close"
            onClick={() => setNameDialogOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
            size="medium"
          >
            <CloseIcon />
          </IconButton>
          <CustomLabel
            text="Account Details"
            fontWeight={600}
            fontSize="1.2rem"
            sx={{ mb: 2 }}
          />
          {selectedAccount && (
            <Box display="flex" flexDirection="column" gap={1}>
              <Box>
                <b>Username:</b> {selectedAccount.systemId}
              </Box>
              {selectedAccount.type === "VENDOR" ? (
                <>
                  <Box>
                    <b>IP:</b> {selectedAccount.ip || "-"}
                  </Box>
                  <Box>
                    <b>Port:</b> {selectedAccount.port || "-"}
                  </Box>
                  <Box>
                    <b>Binds:</b>{" "}
                    <BindValueCell id={selectedAccount.id} fallback="-" />
                  </Box>
                  <Box>
                    <b>TPS:</b> {selectedAccount.tps ?? "-"}
                  </Box>
                </>
              ) : (
                <>
                  <Box>
                    <b>IP:</b> {selectedAccount.acceptedIps || "-"}
                  </Box>
                  <Box>
                    <b>Binds:</b>{" "}
                    <BindValueCell id={selectedAccount.id} fallback="-" />
                  </Box>
                  <Box>
                    <b>TPS:</b> {selectedAccount.tps ?? "-"}
                  </Box>
                  <Box>
                    <b>Hard TPS:</b> {selectedAccount.tpsHardLimit ?? "-"}
                  </Box>
                  <Box>
                    <b>Last Bind Error:</b>{" "}
                    {selectedAccount.lastBindError || "-"}
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>
      </Dialog>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleOpenInNewTab}>Open in New Tab</MenuItem>
        <MenuItem
          onClick={() => {
            if (contextMenu) {
              handleOpenDialog("edit", String(contextMenu.rowId));
              handleContextMenuClose();
            }
          }}
        >
          Edit
        </MenuItem>
      </Menu>
    </Box>
  );
};
export interface AccountDialogRef {
  openDialog: (
    mode: "add" | "edit" | "delete" | "telnet" | "history",
    id?: string,
    isClone?: boolean,
    rowType?: string,
  ) => void;
}

interface AccountDialogProps {
  onRefresh: () => void;
  profileOptions: Option[];
  routingPlanOptions: Option[];
  vpnTunnelOptions: Option[];
  smppScheduleDeliveryTimeOptions: Option[];
  profileMap: Record<
    string,
    {
      name: string;
      accountManagerEmail: string;
      currencyId: string;
      usesVpn?: boolean;
    }
  >;
  appliedFilters: Params<IAccount>;
}

const AccountDialog = forwardRef<AccountDialogRef, AccountDialogProps>(
  (
    {
      onRefresh,
      routingPlanOptions,
      vpnTunnelOptions,
      smppScheduleDeliveryTimeOptions,
      profileOptions,
      appliedFilters,
      profileMap,
    },
    ref,
  ) => {
    const [telnetRowType, setTelnetRowType] = useState<string | undefined>();
    const initialDialogData: IStepDialogData = {
      accountType: "Default",
      name: "",
      type: "",
      isDeleted: false,
      bindType: "1",
      profileId: "",
      hardTps: "",
      ip: "",
      acceptedIps: "",
      systemId: "",
      password: "",
      systemType: "",
      active: true,
      sourceTon: "",
      sourceNpi: "",
      destinationTon: "",
      destinationNpi: "",
      registeredDelivery: false,
      enquireLink: 0,
      tps: 0,
      port: 0,
      // HTTP defaults
      connectionInterface: undefined,
      interfaces: [],
      sendReqHttpMethod: "GET",
      sendReqContentType: "APPLICATION_JSON",
      dlrReqHttpMethod: "GET",
      dlrReqContentType: "APPLICATION_JSON",
      ratePerDelivery: false,
      costPerDelivery: false,
    };
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<
      "add" | "edit" | "delete" | "telnet" | "history"
    >("add");
    const [dialogData, setDialogData] =
      useState<IStepDialogData>(initialDialogData);
    const [selectedId, setSelectedId] = useState<string | undefined>();

    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const [currentStep, setCurrentStep] = useState(0);
    const [isNextDisabled, setIsNextDisabled] = useState<boolean>(true);
    const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(true);

    const stepLabels = getStepLabels(dialogMode, dialogData);
    const hasHttp = getHasHttp(dialogData);

    useImperativeHandle(ref, () => ({
      openDialog: async (
        mode: "add" | "edit" | "delete" | "telnet" | "history",
        id?: string,
        isClone: boolean = false,
        rowType?: string,
      ) => {
        setDialogMode(isClone ? "add" : mode);
        setSelectedId(isClone ? undefined : id);
        setCurrentStep(0);

        const defaultData: IStepDialogData = {
          ...initialDialogData,
          bindType: "BIND_TRX",
          sourceTon: "UNKNOWN",
          destinationTon: "UNKNOWN",
          sourceNpi: "UNKNOWN",
          destinationNpi: "UNKNOWN",
          maximumConnections: 1,
          tps: 50,
          windowSize: 10,
          tpsHardLimit: 100,
          enquireLink: 60,
        };

        try {
          if (mode === "telnet") {
            setTelnetRowType(rowType);
            if (id) {
              const entityData = await getAccountById(
                id,
                dialogData.type === "VENDOR" ? "VENDOR" : "CLIENT",
              );
              setDialogData({
                ...defaultData,
                ...entityData,
                enquireLink: entityData.enquireLink
                  ? Number(entityData.enquireLink) / 1000
                  : undefined,
                id: entityData.id,
                port: entityData.port ? Number(entityData.port) : 0,
                type: entityData.type ?? "CLIENT",
              });
            } else {
              setDialogData(defaultData);
            }
          } else if (
            (mode === "edit" ||
              isClone ||
              mode === "delete" ||
              mode === "history") &&
            id
          ) {
            const fetchAccountData = async () => {
              try {
                return await getAccountById(id, "CLIENT");
              } catch (clientError) {
                try {
                  return await getAccountById(id, "VENDOR");
                } catch (vendorError) {
                  throw new Error("Failed to fetch as CLIENT or VENDOR");
                }
              }
            };

            try {
              const entityData = await fetchAccountData();
              setDialogData({
                ...defaultData,
                ...entityData,
                enquireLink: entityData.enquireLink
                  ? Number(entityData.enquireLink) / 1000
                  : undefined,
                id: isClone ? undefined : entityData.id,
                port: entityData.port ? Number(entityData.port) : 0,
                type: entityData.type ?? "CLIENT",
                // HTTP defaults
                sendReqHttpMethod:
                  entityData.sendRequestField?.httpMethod || "GET",
                sendReqContentType:
                  entityData.sendRequestField?.contentType ||
                  "APPLICATION_JSON",
                dlrReqHttpMethod:
                  entityData.dlrRequestField?.httpMethod || "GET",
                dlrReqContentType:
                  entityData.dlrRequestField?.contentType || "APPLICATION_JSON",
                // Flatten sendRequestField (VENDOR)
                sendReqSendApiKeyName:
                  entityData.sendRequestField?.sendApiKey?.name || "",
                sendReqSendApiKeyLocation:
                  entityData.sendRequestField?.sendApiKey?.location || "URL",
                sendReqUsernameName:
                  entityData.sendRequestField?.username?.name || "",
                sendReqUsernameLocation:
                  entityData.sendRequestField?.username?.location || "URL",
                sendReqPasswordName:
                  entityData.sendRequestField?.password?.name || "",
                sendReqPasswordLocation:
                  entityData.sendRequestField?.password?.location || "URL",
                sendReqDestinationName:
                  entityData.sendRequestField?.destination?.name || "",
                sendReqDestinationLocation:
                  entityData.sendRequestField?.destination?.location || "URL",
                sendReqSourceName:
                  entityData.sendRequestField?.source?.name || "",
                sendReqSourceLocation:
                  entityData.sendRequestField?.source?.location || "URL",
                sendReqTextName: entityData.sendRequestField?.text?.name || "",
                sendReqTextLocation:
                  entityData.sendRequestField?.text?.location || "URL",
                sendReqDataCodingName:
                  entityData.sendRequestField?.dataCoding?.name || "",
                sendReqDataCodingLocation:
                  entityData.sendRequestField?.dataCoding?.location || "URL",
                // Flatten sendResponseField (VENDOR)
                sendRespErrorCode:
                  entityData.sendResponseField?.errorCode || "",
                sendRespErrorDescription:
                  entityData.sendResponseField?.errorDescription || "",
                sendRespMessageErrorCode:
                  entityData.sendResponseField?.messageErrorCode || "",
                sendRespMessageId:
                  entityData.sendResponseField?.messageId || "",
                sendRespOriginatingAddress:
                  entityData.sendResponseField?.originatingAddress || "",
                sendRespDestinationAddress:
                  entityData.sendResponseField?.destinationAddress || "",
                sendRespMessageCount:
                  entityData.sendResponseField?.messageCount || "",
                sendRespMessageParts:
                  entityData.sendResponseField?.messageParts || "",
                // Flatten dlrRequestField (CLIENT)
                dlrReqApiKeyName:
                  entityData.dlrRequestField?.dlrApiKey?.name || "",
                dlrReqApiKeyLocation:
                  entityData.dlrRequestField?.dlrApiKey?.location || "URL",
                dlrReqSenderName:
                  entityData.dlrRequestField?.sender?.name || "",
                dlrReqSenderLocation:
                  entityData.dlrRequestField?.sender?.location || "URL",
                dlrReqDestinationName:
                  entityData.dlrRequestField?.destination?.name || "",
                dlrReqDestinationLocation:
                  entityData.dlrRequestField?.destination?.location || "URL",
                dlrReqMessageIdName:
                  entityData.dlrRequestField?.messageId?.name || "",
                dlrReqMessageIdLocation:
                  entityData.dlrRequestField?.messageId?.location || "URL",
                dlrReqDeliveryStatusName:
                  entityData.dlrRequestField?.deliveryStatus?.name || "",
                dlrReqDeliveryStatusLocation:
                  entityData.dlrRequestField?.deliveryStatus?.location || "URL",
                dlrReqDateReceivedName:
                  entityData.dlrRequestField?.dateReceived?.name || "",
                dlrReqDateReceivedLocation:
                  entityData.dlrRequestField?.dateReceived?.location || "URL",
                dlrReqDescriptionName:
                  entityData.dlrRequestField?.description?.name || "",
                dlrReqDescriptionLocation:
                  entityData.dlrRequestField?.description?.location || "URL",
                dlrReqUsernameName:
                  entityData.dlrRequestField?.username?.name || "",
                dlrReqUsernameLocation:
                  entityData.dlrRequestField?.username?.location || "URL",
                dlrReqPasswordName:
                  entityData.dlrRequestField?.password?.name || "",
                dlrReqPasswordLocation:
                  entityData.dlrRequestField?.password?.location || "URL",
                // Flatten dlrResponseField (CLIENT)
                dlrRespErrorCode: entityData.dlrResponseField?.errorCode || "",
                dlrRespErrorDescription:
                  entityData.dlrResponseField?.errorDescription || "",
              });
            } catch (error) {
              console.error("Error fetching account data:", error);
              ToastNotification.error("Failed to load account details.");
              return; // Exit if data fetch fails
            }
          } else {
            setDialogData(defaultData);
          }

          setDialogOpen(true);
        } catch (error: any) {
          console.error("Error opening dialog:", error);
          ToastNotification.error("Failed to open the dialog.");
        }
      },
    }));

    const profileUsesVpn = !!(
      dialogData.profileId && profileMap[dialogData.profileId]?.usesVpn
    );

    useEffect(() => {
      const stepRequiredFields: Record<number, string[]> = {
        0: ["name", "type", "profileId"],
        1: ["systemId", "password"],
        2: [dialogData.type === "VENDOR" ? "costPerDelivery" : "ratePerDelivery"],
      };

      const requiredFields = stepRequiredFields[currentStep] || [];

      let hasErrors = requiredFields.some((field) => {
        const value = dialogData[field as keyof IStepDialogData];
        return value === null || value === undefined || value === "";
      });

      // Validate Account Mode in step 0
      if (currentStep === 0 && !hasErrors) {
        if (dialogData.type === "VENDOR") {
          if (!dialogData.connectionInterface) hasErrors = true;
        } else if (dialogData.type === "CLIENT") {
          if (
            !dialogData.interfaces ||
            (dialogData.interfaces as string[]).length === 0
          )
            hasErrors = true;
        }
      }

      // Validate VPN Tunnel in step 1 for VENDOR accounts when profile uses VPN
      if (
        currentStep === 1 &&
        !hasErrors &&
        dialogData.type === "VENDOR" &&
        profileUsesVpn
      ) {
        if (
          dialogData.vpnTunnelId === null ||
          dialogData.vpnTunnelId === undefined
        ) {
          hasErrors = true;
        }
      }

      setIsSaveDisabled(hasErrors);
      setIsNextDisabled(hasErrors && currentStep < stepLabels.length - 1);
    }, [dialogData, currentStep, stepLabels, profileUsesVpn]);

    // Guard: reset currentStep if stepLabels shrinks (e.g. HTTP mode removed)
    useEffect(() => {
      if (currentStep >= stepLabels.length) {
        setCurrentStep(stepLabels.length - 1);
      }
    }, [stepLabels.length, currentStep]);

    const handleSave = async () => {
      try {
        console.log("Dialog Data:", dialogData);

        // Destructure out all flattened HTTP fields so they don't end up in the payload
        const {
          sendReqSendApiKeyName,
          sendReqSendApiKeyLocation,
          sendReqUsernameName,
          sendReqUsernameLocation,
          sendReqPasswordName,
          sendReqPasswordLocation,
          sendReqDestinationName,
          sendReqDestinationLocation,
          sendReqSourceName,
          sendReqSourceLocation,
          sendReqTextName,
          sendReqTextLocation,
          sendReqDataCodingName,
          sendReqDataCodingLocation,
          sendReqHttpMethod,
          sendReqContentType,
          sendRespErrorCode,
          sendRespErrorDescription,
          sendRespMessageErrorCode,
          sendRespMessageId,
          sendRespOriginatingAddress,
          sendRespDestinationAddress,
          sendRespMessageCount,
          sendRespMessageParts,
          dlrReqApiKeyName,
          dlrReqApiKeyLocation,
          dlrReqSenderName,
          dlrReqSenderLocation,
          dlrReqDestinationName,
          dlrReqDestinationLocation,
          dlrReqMessageIdName,
          dlrReqMessageIdLocation,
          dlrReqDeliveryStatusName,
          dlrReqDeliveryStatusLocation,
          dlrReqDateReceivedName,
          dlrReqDateReceivedLocation,
          dlrReqDescriptionName,
          dlrReqDescriptionLocation,
          dlrReqUsernameName,
          dlrReqUsernameLocation,
          dlrReqPasswordName,
          dlrReqPasswordLocation,
          dlrReqHttpMethod,
          dlrReqContentType,
          dlrRespErrorCode,
          dlrRespErrorDescription,
          accountType: _accountType,
          ...restDialogData
        } = dialogData;

        const buildLoc = (v?: string) =>
          v ? (v as "URL" | "BODY" | "HEADER") : "URL";

        const dataToSave: Partial<IAccount> = {
          ...restDialogData,
          enquireLink: dialogData.enquireLink
            ? Number(dialogData.enquireLink) * 1000
            : 0,
          type: dialogData.type === "VENDOR" ? "VENDOR" : "CLIENT",
          windowSize:
            dialogData.windowSize !== undefined &&
            dialogData.windowSize !== null
              ? Number(dialogData.windowSize)
              : undefined,
          isDeleted: false,
          // Build nested HTTP objects only when HTTP mode is active
          sendRequestField:
            hasHttp && dialogData.type === "VENDOR"
              ? {
                  sendApiKey: {
                    name: sendReqSendApiKeyName || "",
                    location: buildLoc(sendReqSendApiKeyLocation),
                  },
                  username: {
                    name: sendReqUsernameName || "",
                    location: buildLoc(sendReqUsernameLocation),
                  },
                  password: {
                    name: sendReqPasswordName || "",
                    location: buildLoc(sendReqPasswordLocation),
                  },
                  destination: {
                    name: sendReqDestinationName || "",
                    location: buildLoc(sendReqDestinationLocation),
                  },
                  source: {
                    name: sendReqSourceName || "",
                    location: buildLoc(sendReqSourceLocation),
                  },
                  text: {
                    name: sendReqTextName || "",
                    location: buildLoc(sendReqTextLocation),
                  },
                  dataCoding: {
                    name: sendReqDataCodingName || "",
                    location: buildLoc(sendReqDataCodingLocation),
                  },
                  httpMethod: (sendReqHttpMethod as "GET" | "POST") || "GET",
                  contentType: sendReqContentType || "APPLICATION_JSON",
                }
              : undefined,
          sendResponseField:
            hasHttp && dialogData.type === "VENDOR"
              ? {
                  errorCode: sendRespErrorCode || "",
                  errorDescription: sendRespErrorDescription || "",
                  messageErrorCode: sendRespMessageErrorCode || "",
                  messageId: sendRespMessageId || "",
                  originatingAddress: sendRespOriginatingAddress || "",
                  destinationAddress: sendRespDestinationAddress || "",
                  messageCount: sendRespMessageCount || "",
                  messageParts: sendRespMessageParts || "",
                }
              : undefined,
          dlrRequestField:
            hasHttp && dialogData.type === "CLIENT"
              ? {
                  dlrApiKey: {
                    name: dlrReqApiKeyName || "",
                    location: buildLoc(dlrReqApiKeyLocation),
                  },
                  sender: {
                    name: dlrReqSenderName || "",
                    location: buildLoc(dlrReqSenderLocation),
                  },
                  destination: {
                    name: dlrReqDestinationName || "",
                    location: buildLoc(dlrReqDestinationLocation),
                  },
                  messageId: {
                    name: dlrReqMessageIdName || "",
                    location: buildLoc(dlrReqMessageIdLocation),
                  },
                  deliveryStatus: {
                    name: dlrReqDeliveryStatusName || "",
                    location: buildLoc(dlrReqDeliveryStatusLocation),
                  },
                  dateReceived: {
                    name: dlrReqDateReceivedName || "",
                    location: buildLoc(dlrReqDateReceivedLocation),
                  },
                  description: {
                    name: dlrReqDescriptionName || "",
                    location: buildLoc(dlrReqDescriptionLocation),
                  },
                  username: {
                    name: dlrReqUsernameName || "",
                    location: buildLoc(dlrReqUsernameLocation),
                  },
                  password: {
                    name: dlrReqPasswordName || "",
                    location: buildLoc(dlrReqPasswordLocation),
                  },
                  httpMethod: (dlrReqHttpMethod as "GET" | "POST") || "GET",
                  contentType: dlrReqContentType || "APPLICATION_JSON",
                }
              : undefined,
          dlrResponseField:
            hasHttp && dialogData.type === "CLIENT"
              ? {
                  errorCode: dlrRespErrorCode || "",
                  errorDescription: dlrRespErrorDescription || "",
                }
              : undefined,
          ratePerDelivery:
            dialogData.type === "CLIENT"
              ? dialogData.ratePerDelivery ?? false
              : undefined,
          costPerDelivery:
            dialogData.type === "VENDOR"
              ? dialogData.costPerDelivery ?? false
              : undefined,
        };

        // Strip top-level fields that are empty strings so they are omitted from the payload
        (Object.keys(dataToSave) as (keyof typeof dataToSave)[]).forEach(
          (key) => {
            if (dataToSave[key] === "") {
              delete dataToSave[key];
            }
          },
        );

        if (dialogMode === "edit") {
          if (dialogData.type === "VENDOR") {
            await editProviderApi(
              dataToSave.id as string,
              dataToSave as Partial<IProvider>,
            );
            setDialogOpen(false);
            onRefresh();
            setDialogData({ ...initialDialogData });
          } else {
            await editAccountApi(dataToSave.id as string, dataToSave);
            setDialogOpen(false);
            onRefresh();
            setDialogData({ ...initialDialogData });
          }
        } else if (dialogMode === "add") {
          if (dialogData.type === "VENDOR") {
            await addProviderApi(dataToSave as Partial<IProvider>);
            setDialogOpen(false);
            onRefresh();
            setDialogData({ ...initialDialogData });
          } else {
            await addAccountApi(dataToSave);
            setDialogOpen(false);
            onRefresh();
          }
        }

        ToastNotification.success(
          `Account ${dialogMode === "edit" ? "updated" : "added"} successfully!`,
        );
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };
    const deleteAccount = async () => {
      try {
        if (selectedId) {
          const accountType = dialogData.type as "VENDOR" | "CLIENT";
          console.log("type", accountType);
          if (accountType === "VENDOR") {
            await deleteProviderApi(selectedId);
            onRefresh();
            setDialogOpen(false);
          } else if (accountType === "CLIENT") {
            await deleteAccountApi(selectedId);
            onRefresh();
            setDialogOpen(false);
          } else {
            console.error(
              "Unknown account type for deletion:",
              dialogData.type,
            );
            ToastNotification.error("Unknown account type. Unable to delete.");
            return;
          }

          ToastNotification.success(
            `Account (${accountType}) deleted successfully!`,
          );
        }
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const dialogButtons: ICustomButtonProps[] =
      dialogMode === "history"
        ? []
        : dialogMode === "telnet"
          ? []
          : dialogMode === "delete"
            ? [
                {
                  label: "Delete",
                  onClick: deleteAccount,
                  variant: "contained",
                  bgColor: COLORS.GOLD,
                  disabled: !hasJwtPermission("DELETE_CONNECTION_PROFILE"),
                },
              ]
            : [
                ...(currentStep > 0
                  ? [
                      {
                        label: "Back",
                        onClick: () => setCurrentStep((prev) => prev - 1),
                        bgColor: COLORS.GOLD,
                      },
                    ]
                  : []),
                ...(currentStep < stepLabels.length - 1
                  ? [
                      {
                        label: "Next",
                        onClick: () => setCurrentStep((prev) => prev + 1),
                        bgColor: COLORS.GOLD,
                        disabled: isNextDisabled,
                      },
                    ]
                  : [
                      {
                        label: "Finish",
                        onClick: () => {
                          handleSave();
                        },
                        bgColor: COLORS.GOLD,
                        disabled:
                          isSaveDisabled ||
                          (dialogMode === "edit"
                            ? !hasJwtPermission("PUT_CONNECTION_PROFILE")
                            : !hasJwtPermission("POST_CONNECTION_PROFILE")),
                      },
                    ]),
              ];

    useEffect(() => {
      if (dialogMode === "add") {
        setDialogData((prev) => {
          let rawName = prev.name || "";
          if (rawName.endsWith("-MO") || rawName.endsWith("-MT")) {
            rawName = rawName.slice(0, -3);
          }
          let newName = rawName;
          if (prev.type === "CLIENT") {
            newName = rawName + "-MO";
          } else if (prev.type === "VENDOR") {
            newName = rawName + "-MT";
          }
          return { ...prev, name: newName };
        });
      }
    }, [dialogData.type, dialogMode]);

    type FlattenedAccountHistoryRow = IAccount & {
      historyTimestamp: Date;
      // sendRequestField flattened (VENDOR)
      histSendReqSendApiKeyName: string;
      histSendReqSendApiKeyLocation: string;
      histSendReqUsernameName: string;
      histSendReqUsernameLocation: string;
      histSendReqPasswordName: string;
      histSendReqPasswordLocation: string;
      histSendReqDestinationName: string;
      histSendReqDestinationLocation: string;
      histSendReqSourceName: string;
      histSendReqSourceLocation: string;
      histSendReqTextName: string;
      histSendReqTextLocation: string;
      histSendReqDataCodingName: string;
      histSendReqDataCodingLocation: string;
      histSendReqHttpMethod: string;
      histSendReqContentType: string;
      // sendResponseField flattened (VENDOR)
      histSendRespErrorCode: string;
      histSendRespErrorDescription: string;
      histSendRespMessageErrorCode: string;
      histSendRespMessageId: string;
      histSendRespOriginatingAddress: string;
      histSendRespDestinationAddress: string;
      histSendRespMessageCount: string;
      histSendRespMessageParts: string;
      // dlrRequestField flattened (CLIENT)
      histDlrReqApiKeyName: string;
      histDlrReqApiKeyLocation: string;
      histDlrReqSenderName: string;
      histDlrReqSenderLocation: string;
      histDlrReqDestinationName: string;
      histDlrReqDestinationLocation: string;
      histDlrReqMessageIdName: string;
      histDlrReqMessageIdLocation: string;
      histDlrReqDeliveryStatusName: string;
      histDlrReqDeliveryStatusLocation: string;
      histDlrReqDateReceivedName: string;
      histDlrReqDateReceivedLocation: string;
      histDlrReqDescriptionName: string;
      histDlrReqDescriptionLocation: string;
      histDlrReqUsernameName: string;
      histDlrReqUsernameLocation: string;
      histDlrReqPasswordName: string;
      histDlrReqPasswordLocation: string;
      histDlrReqHttpMethod: string;
      histDlrReqContentType: string;
      // dlrResponseField flattened (CLIENT)
      histDlrRespErrorCode: string;
      histDlrRespErrorDescription: string;
    };

    const fieldHistory: IField<FlattenedAccountHistoryRow>[] = [
      { key: "name", header: "Name" },
      {
        key: "profileId",
        header: "Account Profile",
        transform: async (t) => {
          if (!t.profileId) return "Unknown";
          const profile = profileMap[t.profileId];
          return profile?.name || "Unknown";
        },
      },
      {
        key: "profileId",
        header: "Account Manager",
        transform: async (t) => {
          if (!t.profileId) return "Unknown";
          const profile = profileMap[t.profileId];
          return profile?.accountManagerEmail;
        },
      },
      ...(dialogData.type === "CLIENT"
        ? ([
            {
              key: "routingPlanId" as keyof FlattenedAccountHistoryRow,
              header: "Routing Plan",
              transform: (r: FlattenedAccountHistoryRow) =>
                routingPlanOptions.find(
                  (option) => String(option.value) === String(r.routingPlanId),
                )?.label || "-",
            },
          ] as IField<FlattenedAccountHistoryRow>[])
        : []),
      { key: "type", header: "Account Type" },
      { key: "tps", header: "TPS" },
      { key: "tpsHardLimit", header: "Hard TPS" },
      { key: "systemId", header: "Username" },
      { key: "password", header: "Password" },
      { key: "windowSize", header: "Window Size" },
      ...(dialogData.type === "VENDOR"
        ? [
            {
              key: "ip" as keyof FlattenedAccountHistoryRow,
              header: "IP Address",
            },
          ]
        : []),
      ...(dialogData.type === "CLIENT"
        ? [
            {
              key: "acceptedIps" as keyof FlattenedAccountHistoryRow,
              header: "IP Address",
            },
          ]
        : []),
      { key: "port", header: "Port" },
      { key: "bindType", header: "Connection Mode" },
      { key: "sourceNpi", header: "Source NPI" },
      { key: "sourceTon", header: "Source TON" },
      { key: "destinationNpi", header: "Destination NPI" },
      { key: "destinationTon", header: "Destination TON" },
      { key: "enquireLink", header: "Enquire Link" },
      ...(dialogData.type === "VENDOR"
        ? ([
            {
              key: "smppScheduleDeliveryTime" as keyof FlattenedAccountHistoryRow,
              header: "SMPP Schedule Delivery Time",
              transform: (row: FlattenedAccountHistoryRow) =>
                row.smppScheduleDeliveryTime || "-",
            },
          ] as IField<FlattenedAccountHistoryRow>[])
        : []),
      // Account Mode
      ...(dialogData.type === "VENDOR"
        ? ([
            {
              key: "connectionInterface" as keyof FlattenedAccountHistoryRow,
              header: "Account Mode",
              transform: (row: FlattenedAccountHistoryRow) =>
                row.connectionInterface || "-",
            },
          ] as IField<FlattenedAccountHistoryRow>[])
        : []),
      ...(dialogData.type === "CLIENT"
        ? ([
            {
              key: "interfaces" as keyof FlattenedAccountHistoryRow,
              header: "Account Mode",
              transform: (row: FlattenedAccountHistoryRow) =>
                Array.isArray(row.interfaces) && row.interfaces.length > 0
                  ? (row.interfaces as string[]).join(", ")
                  : "-",
            },
          ] as IField<FlattenedAccountHistoryRow>[])
        : []),
      // HTTP Details
      ...(hasHttp && dialogData.type === "VENDOR"
        ? ([
            {
              key: "httpUsername" as keyof FlattenedAccountHistoryRow,
              header: "HTTP Username",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.httpUsername || "-",
            },
            {
              key: "httpPassword" as keyof FlattenedAccountHistoryRow,
              header: "HTTP Password",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.httpPassword || "-",
            },
            {
              key: "sendBaseUrl" as keyof FlattenedAccountHistoryRow,
              header: "Send Base URL",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.sendBaseUrl || "-",
            },
            {
              key: "sendApiKey" as keyof FlattenedAccountHistoryRow,
              header: "Send API Key",
              transform: (r: FlattenedAccountHistoryRow) => r.sendApiKey || "-",
            },
          ] as IField<FlattenedAccountHistoryRow>[])
        : []),
      ...(hasHttp && dialogData.type === "CLIENT"
        ? ([
            {
              key: "dlrBaseUrl" as keyof FlattenedAccountHistoryRow,
              header: "DLR Base URL",
              transform: (r: FlattenedAccountHistoryRow) => r.dlrBaseUrl || "-",
            },
            {
              key: "dlrApiKey" as keyof FlattenedAccountHistoryRow,
              header: "DLR API Key",
              transform: (r: FlattenedAccountHistoryRow) => r.dlrApiKey || "-",
            },
          ] as IField<FlattenedAccountHistoryRow>[])
        : []),
      // HTTP Request Fields
      ...(hasHttp && dialogData.type === "VENDOR"
        ? ([
            {
              key: "histSendReqSendApiKeyName",
              header: "Send API Key Field Name",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histSendReqSendApiKeyName || "-",
            },
            {
              key: "histSendReqSendApiKeyLocation",
              header: "Send API Key Location",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histSendReqSendApiKeyLocation || "-",
            },
            {
              key: "histSendReqUsernameName",
              header: "Username Field Name",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histSendReqUsernameName || "-",
            },
            {
              key: "histSendReqUsernameLocation",
              header: "Username Location",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histSendReqUsernameLocation || "-",
            },
            {
              key: "histSendReqPasswordName",
              header: "Password Field Name",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histSendReqPasswordName || "-",
            },
            {
              key: "histSendReqPasswordLocation",
              header: "Password Location",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histSendReqPasswordLocation || "-",
            },
            {
              key: "histSendReqDestinationName",
              header: "Destination Field Name",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histSendReqDestinationName || "-",
            },
            {
              key: "histSendReqDestinationLocation",
              header: "Destination Location",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histSendReqDestinationLocation || "-",
            },
            {
              key: "histSendReqSourceName",
              header: "Source Field Name",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histSendReqSourceName || "-",
            },
            {
              key: "histSendReqSourceLocation",
              header: "Source Location",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histSendReqSourceLocation || "-",
            },
            {
              key: "histSendReqTextName",
              header: "Text Field Name",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histSendReqTextName || "-",
            },
            {
              key: "histSendReqTextLocation",
              header: "Text Location",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histSendReqTextLocation || "-",
            },
            {
              key: "histSendReqDataCodingName",
              header: "Data Coding Field Name",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histSendReqDataCodingName || "-",
            },
            {
              key: "histSendReqDataCodingLocation",
              header: "Data Coding Location",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histSendReqDataCodingLocation || "-",
            },
            {
              key: "histSendReqHttpMethod",
              header: "Send HTTP Method",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histSendReqHttpMethod || "-",
            },
            {
              key: "histSendReqContentType",
              header: "Send Content Type",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histSendReqContentType || "-",
            },
          ] as IField<FlattenedAccountHistoryRow>[])
        : []),
      ...(hasHttp && dialogData.type === "CLIENT"
        ? ([
            {
              key: "histDlrReqApiKeyName",
              header: "DLR API Key Field Name",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histDlrReqApiKeyName || "-",
            },
            {
              key: "histDlrReqApiKeyLocation",
              header: "DLR API Key Location",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histDlrReqApiKeyLocation || "-",
            },
            {
              key: "histDlrReqSenderName",
              header: "Sender Field Name",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histDlrReqSenderName || "-",
            },
            {
              key: "histDlrReqSenderLocation",
              header: "Sender Location",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histDlrReqSenderLocation || "-",
            },
            {
              key: "histDlrReqDestinationName",
              header: "DLR Destination Field Name",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histDlrReqDestinationName || "-",
            },
            {
              key: "histDlrReqDestinationLocation",
              header: "DLR Destination Location",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histDlrReqDestinationLocation || "-",
            },
            {
              key: "histDlrReqMessageIdName",
              header: "Message ID Field Name",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histDlrReqMessageIdName || "-",
            },
            {
              key: "histDlrReqMessageIdLocation",
              header: "Message ID Location",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histDlrReqMessageIdLocation || "-",
            },
            {
              key: "histDlrReqDeliveryStatusName",
              header: "Delivery Status Field Name",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histDlrReqDeliveryStatusName || "-",
            },
            {
              key: "histDlrReqDeliveryStatusLocation",
              header: "Delivery Status Location",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histDlrReqDeliveryStatusLocation || "-",
            },
            {
              key: "histDlrReqDateReceivedName",
              header: "Date Received Field Name",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histDlrReqDateReceivedName || "-",
            },
            {
              key: "histDlrReqDateReceivedLocation",
              header: "Date Received Location",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histDlrReqDateReceivedLocation || "-",
            },
            {
              key: "histDlrReqDescriptionName",
              header: "Description Field Name",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histDlrReqDescriptionName || "-",
            },
            {
              key: "histDlrReqDescriptionLocation",
              header: "Description Location",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histDlrReqDescriptionLocation || "-",
            },
            {
              key: "histDlrReqUsernameName",
              header: "DLR Username Field Name",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histDlrReqUsernameName || "-",
            },
            {
              key: "histDlrReqUsernameLocation",
              header: "DLR Username Location",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histDlrReqUsernameLocation || "-",
            },
            {
              key: "histDlrReqPasswordName",
              header: "DLR Password Field Name",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histDlrReqPasswordName || "-",
            },
            {
              key: "histDlrReqPasswordLocation",
              header: "DLR Password Location",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histDlrReqPasswordLocation || "-",
            },
            {
              key: "histDlrReqHttpMethod",
              header: "DLR HTTP Method",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histDlrReqHttpMethod || "-",
            },
            {
              key: "histDlrReqContentType",
              header: "DLR Content Type",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histDlrReqContentType || "-",
            },
          ] as IField<FlattenedAccountHistoryRow>[])
        : []),
      // HTTP Response Fields
      ...(hasHttp && dialogData.type === "VENDOR"
        ? ([
            {
              key: "histSendRespErrorCode",
              header: "Send Error Code Field",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histSendRespErrorCode || "-",
            },
            {
              key: "histSendRespErrorDescription",
              header: "Send Error Description Field",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histSendRespErrorDescription || "-",
            },
            {
              key: "histSendRespMessageErrorCode",
              header: "Send Message Error Code Field",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histSendRespMessageErrorCode || "-",
            },
            {
              key: "histSendRespMessageId",
              header: "Send Message ID Field",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histSendRespMessageId || "-",
            },
            {
              key: "histSendRespOriginatingAddress",
              header: "Originating Address Field",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histSendRespOriginatingAddress || "-",
            },
            {
              key: "histSendRespDestinationAddress",
              header: "Destination Address Field",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histSendRespDestinationAddress || "-",
            },
            {
              key: "histSendRespMessageCount",
              header: "Message Count Field",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histSendRespMessageCount || "-",
            },
            {
              key: "histSendRespMessageParts",
              header: "Message Parts Field",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histSendRespMessageParts || "-",
            },
          ] as IField<FlattenedAccountHistoryRow>[])
        : []),
      ...(hasHttp && dialogData.type === "CLIENT"
        ? ([
            {
              key: "histDlrRespErrorCode",
              header: "DLR Error Code Field",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histDlrRespErrorCode || "-",
            },
            {
              key: "histDlrRespErrorDescription",
              header: "DLR Error Description Field",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.histDlrRespErrorDescription || "-",
            },
          ] as IField<FlattenedAccountHistoryRow>[])
        : []),
      ...(dialogData.type === "CLIENT"
        ? ([
            {
              key: "ratePerDelivery" as keyof FlattenedAccountHistoryRow,
              header: "Rate Per Delivery",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.ratePerDelivery ? "Yes" : "No",
            },
          ] as IField<FlattenedAccountHistoryRow>[])
        : []),
      ...(dialogData.type === "VENDOR"
        ? ([
            {
              key: "costPerDelivery" as keyof FlattenedAccountHistoryRow,
              header: "Cost Per Delivery",
              transform: (r: FlattenedAccountHistoryRow) =>
                r.costPerDelivery ? "Yes" : "No",
            },
          ] as IField<FlattenedAccountHistoryRow>[])
        : []),
      { key: "historyTimestamp", header: "Date Modified" },
      { key: "createdBy", header: "Created By" },
      { key: "updatedBy", header: "Updated By" },
    ];

    return (
      <CustomDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setCurrentStep(0);
        }}
        mode={dialogMode}
        dialogData={{ accountType: "account" }}
        {...(dialogMode !== "telnet" && dialogMode !== "history"
          ? {
              onNext: () =>
                setCurrentStep((prev) =>
                  prev < stepLabels.length - 1 ? prev + 1 : prev,
                ),
              onBack: () =>
                setCurrentStep((prev) => (prev > 0 ? prev - 1 : prev)),
              stepLabels: stepLabels,
              activeStep: currentStep,
            }
          : {})}
        title={
          dialogMode === "telnet"
            ? `${dialogData.name} - Session Logs`
            : dialogMode === "history"
              ? "History"
              : undefined
        }
        fields={
          dialogMode === "history"
            ? [
                {
                  type: "custom",
                  component: (
                    <History
                      filters={appliedFilters as Params<IAccount>}
                      fields={fieldHistory}
                      fetchFunc={({ size = 10 }) =>
                        fetchAccountHistory(String(selectedId), {
                          size,
                        }).then((result) => ({
                          ...result,
                          content: result.content.map((item: any) => ({
                            ...item,
                            historyTimestamp:
                              item.historyTimestamp ?? new Date(),
                            ratePerDelivery: Boolean(item.ratePerDelivery),
                            costPerDelivery: Boolean(item.costPerDelivery),
                            // Flatten sendRequestField (VENDOR)
                            histSendReqSendApiKeyName:
                              item.sendRequestField?.sendApiKey?.name || "",
                            histSendReqSendApiKeyLocation:
                              item.sendRequestField?.sendApiKey?.location || "",
                            histSendReqUsernameName:
                              item.sendRequestField?.username?.name || "",
                            histSendReqUsernameLocation:
                              item.sendRequestField?.username?.location || "",
                            histSendReqPasswordName:
                              item.sendRequestField?.password?.name || "",
                            histSendReqPasswordLocation:
                              item.sendRequestField?.password?.location || "",
                            histSendReqDestinationName:
                              item.sendRequestField?.destination?.name || "",
                            histSendReqDestinationLocation:
                              item.sendRequestField?.destination?.location ||
                              "",
                            histSendReqSourceName:
                              item.sendRequestField?.source?.name || "",
                            histSendReqSourceLocation:
                              item.sendRequestField?.source?.location || "",
                            histSendReqTextName:
                              item.sendRequestField?.text?.name || "",
                            histSendReqTextLocation:
                              item.sendRequestField?.text?.location || "",
                            histSendReqDataCodingName:
                              item.sendRequestField?.dataCoding?.name || "",
                            histSendReqDataCodingLocation:
                              item.sendRequestField?.dataCoding?.location || "",
                            histSendReqHttpMethod:
                              item.sendRequestField?.httpMethod || "",
                            histSendReqContentType:
                              item.sendRequestField?.contentType || "",
                            // Flatten sendResponseField (VENDOR)
                            histSendRespErrorCode:
                              item.sendResponseField?.errorCode || "",
                            histSendRespErrorDescription:
                              item.sendResponseField?.errorDescription || "",
                            histSendRespMessageErrorCode:
                              item.sendResponseField?.messageErrorCode || "",
                            histSendRespMessageId:
                              item.sendResponseField?.messageId || "",
                            histSendRespOriginatingAddress:
                              item.sendResponseField?.originatingAddress || "",
                            histSendRespDestinationAddress:
                              item.sendResponseField?.destinationAddress || "",
                            histSendRespMessageCount:
                              item.sendResponseField?.messageCount || "",
                            histSendRespMessageParts:
                              item.sendResponseField?.messageParts || "",
                            // Flatten dlrRequestField (CLIENT)
                            histDlrReqApiKeyName:
                              item.dlrRequestField?.dlrApiKey?.name || "",
                            histDlrReqApiKeyLocation:
                              item.dlrRequestField?.dlrApiKey?.location || "",
                            histDlrReqSenderName:
                              item.dlrRequestField?.sender?.name || "",
                            histDlrReqSenderLocation:
                              item.dlrRequestField?.sender?.location || "",
                            histDlrReqDestinationName:
                              item.dlrRequestField?.destination?.name || "",
                            histDlrReqDestinationLocation:
                              item.dlrRequestField?.destination?.location || "",
                            histDlrReqMessageIdName:
                              item.dlrRequestField?.messageId?.name || "",
                            histDlrReqMessageIdLocation:
                              item.dlrRequestField?.messageId?.location || "",
                            histDlrReqDeliveryStatusName:
                              item.dlrRequestField?.deliveryStatus?.name || "",
                            histDlrReqDeliveryStatusLocation:
                              item.dlrRequestField?.deliveryStatus?.location ||
                              "",
                            histDlrReqDateReceivedName:
                              item.dlrRequestField?.dateReceived?.name || "",
                            histDlrReqDateReceivedLocation:
                              item.dlrRequestField?.dateReceived?.location ||
                              "",
                            histDlrReqDescriptionName:
                              item.dlrRequestField?.description?.name || "",
                            histDlrReqDescriptionLocation:
                              item.dlrRequestField?.description?.location || "",
                            histDlrReqUsernameName:
                              item.dlrRequestField?.username?.name || "",
                            histDlrReqUsernameLocation:
                              item.dlrRequestField?.username?.location || "",
                            histDlrReqPasswordName:
                              item.dlrRequestField?.password?.name || "",
                            histDlrReqPasswordLocation:
                              item.dlrRequestField?.password?.location || "",
                            histDlrReqHttpMethod:
                              item.dlrRequestField?.httpMethod || "",
                            histDlrReqContentType:
                              item.dlrRequestField?.contentType || "",
                            // Flatten dlrResponseField (CLIENT)
                            histDlrRespErrorCode:
                              item.dlrResponseField?.errorCode || "",
                            histDlrRespErrorDescription:
                              item.dlrResponseField?.errorDescription || "",
                          })),
                        }))
                      }
                    />
                  ),
                },
              ]
            : dialogMode === "delete"
              ? [
                  {
                    type: "custom",
                    component: (
                      <Box>Are you sure you want to delete this Account?</Box>
                    ),
                  },
                ]
              : dialogMode === "telnet"
                ? [
                    {
                      type: "custom",
                      component: (
                        <TelnetDialogContent
                          id={String(selectedId)}
                          rowType={telnetRowType}
                          resetPermissionScope="ACCOUNT"
                          onExportClick={() => {
                            if (selectedId) {
                              exportSessionLogs({
                                connectionProfileId: Number(selectedId),
                              });
                            }
                          }}
                        />
                      ),
                    },
                  ]
                : getStepsData(
                    dialogData,
                    dialogMode,
                    errors,
                    setDialogData,
                    setErrors,
                    routingPlanOptions,
                    profileOptions,
                    vpnTunnelOptions,
                    profileUsesVpn,
                    smppScheduleDeliveryTimeOptions,
                  )[currentStep] || []
        }
        fieldsPerRow={dialogMode === "delete" ? undefined : 2}
        buttons={dialogButtons}
      />
    );
  },
);

export default AccountPage;
